// Fetch and list the jobs to be displayed in Scheduler Dashboard
import { useEffect, useMemo, useState } from 'react';

import { ChevronDownIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import Image from 'next/image';

import { partition, times } from 'lodash';

import { JobDoc } from '@isd/lib/firebase/db/metaTypes';

import isdLogo from '@public/isd-logo-primary-light.svg';

import { useEditSchedulerContext } from '@/context/EditSchedulerContext';
import { useSchedulerContext } from '@/context/SchedulerContext';

import EditDispatchScheduleActionButtons from '@/components/Job/JobsList/EditDispatchScheduleActionButtons';
import TotalTrucksCount from '@/components/Job/JobsList/TotalTrucksCount';
import { useScheduledJobs } from '@/components/Job/useScheduledJobs';
import JobCard from '@/components/Scheduler/SchedulerMain/JobCard';

import { reactAppJobsListRoute, reactAppUrl } from '@/env';

export default function JobsList() {
  const { schedulerCurrentDate } = useSchedulerContext();
  const { isLoading: isEditSchedulerLoading } = useEditSchedulerContext();
  const { jobs: jobsFiltered, isLoading } = useScheduledJobs();

  return isLoading || !!jobsFiltered.length ? (
    <JobsListContent
      jobs={jobsFiltered}
      isLoading={isLoading || isEditSchedulerLoading}
    />
  ) : (
    <EmptyJobsListContent selectedDate={schedulerCurrentDate} />
  );
}

function JobsListContent({
  jobs,
  isLoading,
}: {
  jobs: JobDoc[];
  isLoading: boolean;
}) {
  const {
    canUserEditDispatchSchedule,
    selectedGroup,
    selectedJob,
    operationsRecordList,
  } = useEditSchedulerContext();
  const { pinnedJobIds } = useSchedulerContext();
  const [showUnpinnedJobs, setShowUnpinnedJobs] = useState(false);

  // With pinned jobs, show only those, and keep the rest a click away to pin more
  const hasPins = !isLoading && !!pinnedJobIds.length;
  const [pinnedJobs, unpinnedJobs] = useMemo(
    () =>
      hasPins
        ? partition(jobs, (job) => pinnedJobIds.includes(job.id))
        : [jobs, []],
    [jobs, pinnedJobIds, hasPins]
  );
  const shownJobs = hasPins ? pinnedJobs : jobs;
  const unpinnedLabel = `${unpinnedJobs.length} unpinned ${unpinnedJobs.length === 1 ? 'job' : 'jobs'}`;

  useEffect(() => {
    if (!pinnedJobIds.length) {
      setShowUnpinnedJobs(false);
    }
  }, [pinnedJobIds.length]);

  const cardsGridClassName = clsx(
    'grid w-full gap-y-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4',
    selectedGroup !== null ? 'gap-x-3' : 'gap-x-4 2xl:gap-x-8'
  );

  return (
    <div
      className={clsx(
        'jobs-list bg-base-150 min-h-full w-full items-start rounded-sm px-3 pt-5 pb-10',
        // Use grid layout only when there are enough jobs to cover the entire container
        (isLoading || shownJobs.length > 8) && 'grid',
        selectedGroup === null && 'md:px-10 2xl:px-12'
      )}
    >
      <div className="flex pb-5">
        <div className="w-full">
          {canUserEditDispatchSchedule &&
            (!selectedJob && !operationsRecordList.length ? (
              <div className="text-xl">{'Click to Select'}</div>
            ) : (
              <EditDispatchScheduleActionButtons jobs={jobs} />
            ))}
        </div>
        <TotalTrucksCount jobs={shownJobs} isLoading={isLoading} />
      </div>
      {hasPins && !pinnedJobs.length && (
        <div className="pb-4 text-sm opacity-70">
          {'None of the pinned jobs match the filters.'}
        </div>
      )}
      <div className={clsx(cardsGridClassName, 'h-full')}>
        {isLoading
          ? times(12, (idx) => (
              <div
                key={idx}
                className="skeleton bg-base-200 h-full min-h-40 w-full rounded-2xl"
              ></div>
            ))
          : shownJobs.map((job) => <JobCard key={job.id} jobDoc={job} />)}
      </div>
      {hasPins && !!unpinnedJobs.length && (
        <div className="unpinned-jobs pt-8">
          <button
            type="button"
            className="btn btn-ghost btn-sm mb-4"
            onClick={() => setShowUnpinnedJobs(!showUnpinnedJobs)}
            aria-expanded={showUnpinnedJobs}
          >
            <ChevronDownIcon
              className={clsx(
                'size-5 transition duration-300',
                showUnpinnedJobs && 'scale-y-[-1]'
              )}
              aria-hidden="true"
            />
            {showUnpinnedJobs
              ? `Hide the ${unpinnedLabel}`
              : `Show the ${unpinnedLabel} to pin more`}
          </button>
          {showUnpinnedJobs && (
            <div className={cardsGridClassName}>
              {unpinnedJobs.map((job) => (
                <JobCard key={job.id} jobDoc={job} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyJobsListContent({ selectedDate }: { selectedDate: Date }) {
  const onClickAddJob = () => {
    // Redirect to react-app's Jobs List page with currently selected date as filter
    const dateStr = selectedDate.getDateISOStr();
    const urlParams = new URLSearchParams({
      selectedJobViewName: 'By Date',
      startDate: dateStr,
      endDate: dateStr,
    });
    const addJobUrl = `${reactAppUrl}${reactAppJobsListRoute}?${urlParams}`;
    window.open(addJobUrl);
  };

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="prose flex h-full flex-col items-center justify-evenly self-center md:h-auto">
        <Image
          src={isdLogo}
          className="desktop-logo hidden md:block"
          width={400}
          alt={'isd-logo'}
        />
        <Image
          src={isdLogo}
          className="mobile-logo md:hidden"
          width={300}
          alt={'isd-logo'}
        />
        <h4 className="mt-0">
          {'First, '}
          <span
            onClick={onClickAddJob}
            className="clickable text-primary hover:underline"
          >
            {'add a job'}
          </span>
          {' to schedule your dispatches.'}
        </h4>
      </div>
    </div>
  );
}
